<script setup lang="ts">
  import { computed, reactive, ref, watch, watchEffect } from 'vue'
  import { Button, message } from 'ant-design-vue'
  import { Trash2 } from 'lucide-vue-next'
  import {
    PlusOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    HolderOutlined,
    CompressOutlined,
    ExpandOutlined
  } from '@ant-design/icons-vue'
  import { CalculationField } from '../models/CalculationField'
  import { CalculationsService } from '../services/CalculationsService'
  import { defaultMessageDurationSeconds } from '@/utils'
  import CalculationConfiguration from './CalculationConfiguration.vue'
  import { CalculationSchema } from '../models/CalculationSchema'
  import { Calculation } from '../models/Calculation'
  import { CalculationFieldType } from '../models/CalculationFieldType'

  interface IArrayCalculationBuilderProps {
    calculationSchemas: Array<CalculationSchema>
    availableColumns: Array<{ value: string; label: string }>
    availableParams: Array<{ value: string; label: string }>
    calculationField: CalculationField
    modelValue: []
    configurationLevel: number
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
  }

  interface IArrayCalculationBuilderEmits {
    (e: 'update:modelValue', value: Array<any>): void
    (
      e: 'child-calculation-type-changed',
      type: string,
      calculation: Calculation,
      configuration: {}
    ): void
  }

  const props = defineProps<IArrayCalculationBuilderProps>()
  const emit = defineEmits<IArrayCalculationBuilderEmits>()

  const arrayCalculationField = reactive<CalculationField>(props.calculationField)

  if (arrayCalculationField.field_type !== CalculationFieldType.Array)
    throw new Error('ArrayCalculationBuilder requires an Array type CustomCalculationField!')
  if (arrayCalculationField.item_schema == null)
    throw new Error('Array type CustomCalculationFields require an item_schema!')

  const calculations = ref<Array<Calculation>>([])

  const isCollapsed = ref<boolean>(false)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }

  // Drag / reorder state
  const draggedIndex = ref<number | null>(null)
  const dragOverIndex = ref<number | null>(null)
  const instanceId = `arrayCalcualtion-${Math.random().toString(36).slice(2)}`

  const moveItem = (from: number, to: number) => {
    if (from === to) return
    const item = calculations.value.splice(from, 1)[0]
    calculations.value.splice(to, 0, item)
  }

  const moveUp = (index: number) => {
    if (index <= 0) return
    moveItem(index, index - 1)
  }

  const moveDown = (index: number) => {
    if (index >= calculations.value.length - 1) return
    moveItem(index, index + 1)
  }

  const handleDragLeave = () => {
    dragOverIndex.value = null
  }

  const handleDragStart = (event: DragEvent, index: number) => {
    event.stopPropagation()
    draggedIndex.value = index
    const id = instanceId
    // store origin in dataTransfer and window fallback
    try {
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', String(index))
        event.dataTransfer.setData('application/x-array-builder-id', id)
      }
    } catch {}
    ;(window as any).__arrayBuilderDragSourceId = id
  }

  const handleDragOver = (event: DragEvent, index: number) => {
    event.preventDefault()
    event.stopPropagation()
    let sourceId: string | null = null
    try {
      sourceId = event.dataTransfer?.getData('application/x-array-builder-id') || null
    } catch {}
    sourceId = sourceId || (window as any).__arrayBuilderDragSourceId || null
    if (sourceId !== instanceId) {
      dragOverIndex.value = -1
      return
    }
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
    dragOverIndex.value = index
  }

  const handleDrop = (event: DragEvent, targetIndex: number) => {
    event.preventDefault()
    event.stopPropagation()
    let sourceId: string | null = null
    try {
      sourceId = event.dataTransfer?.getData('application/x-array-builder-id') || null
    } catch {}
    sourceId = sourceId || (window as any).__arrayBuilderDragSourceId || null
    if (sourceId !== instanceId) return

    if (draggedIndex.value == null) return
    const from = draggedIndex.value
    const to = targetIndex
    if (from === to) {
      draggedIndex.value = null
      dragOverIndex.value = -1
      ;(window as any).__arrayBuilderDragSourceId = null
      return
    }
    moveItem(from, to)
    draggedIndex.value = null
    dragOverIndex.value = -1
    ;(window as any).__arrayBuilderDragSourceId = null
  }

  const handleDragEnd = () => {
    draggedIndex.value = null
    dragOverIndex.value = -1
    ;(window as any).__arrayBuilderDragSourceId = null
  }

  watch(
    props.modelValue,
    (newVal) => {
      calculations.value = []
      if (Array.isArray(newVal)) {
        newVal.forEach((value: any) => {
          const calculation = CalculationsService.createCalculationFromCalculationField(
            arrayCalculationField.item_schema!
          )
          calculation.configuration =
            value.configuration != null
              ? CalculationsService.getFlattenedCalculation(value, null)
              : value
          calculations.value.push(calculation)
        })
      }
    },
    { immediate: true, deep: true }
  )

  watch(
    calculations,
    (newValue) => {
      emit('update:modelValue', newValue)
    },
    { deep: true }
  )

  const initializeConfiguration = (calculation: Calculation, fields: Array<CalculationField>) => {
    if (calculation == null) throw new Error('calculation is required')
    if (arrayCalculationField.item_schema?.field_type !== CalculationFieldType.Object) return
    const config = CalculationsService.initializeCalculationFieldConfiguration(fields)
    calculation.configuration = config
  }

  const isAddDisabled = computed(() => {
    const maxItems = (arrayCalculationField.field_type_args as any)?.array_max_items
    if (maxItems === undefined || maxItems === null) return false
    if (typeof maxItems !== 'number' || isNaN(maxItems) || maxItems < 0) return false
    return calculations.value.length >= maxItems
  })

  const addCalculation = () => {
    const newCalculation = CalculationsService.createCalculationFromCalculationField(
      arrayCalculationField.item_schema!
    )
    initializeConfiguration(newCalculation, arrayCalculationField.item_schema?.fields || [])
    calculations.value.push(newCalculation)
    message.success(
      `Added calculation: ${arrayCalculationField.display_name}`,
      defaultMessageDurationSeconds
    )
    return newCalculation
  }

  const removeCalculation = (index: number) => {
    calculations.value.splice(index, 1)
  }

  const handleConfigurationUpdate = (
    calculation: Calculation,
    config: Record<string, any>,
    calculationTypeChange?: boolean
  ) => {
    const calculationIndex = calculations.value.findIndex((c) => c.id === calculation.id)
    if (calculationIndex > -1) {
      calculations.value[calculationIndex].configuration = { ...config }
    }
    if (calculationTypeChange === true) {
      emit('child-calculation-type-changed', calculation.type, calculation, { ...config })
    }
  }
</script>

<template>
  <div
    v-if="arrayCalculationField.item_schema?.field_type === CalculationFieldType.Object"
    class="calculation-array-object-builder-container"
  >
    <div class="mt-[-40px] self-end flex items-center gap-2">
      <a-tooltip placement="top">
        <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
        <Button
          style="padding: 3px; border: none"
          type="text"
          size="large"
          class="flex items-center justify-center"
          @click="toggleGlobalCollapse"
          :disabled="calculations.length === 0"
          :aria-disabled="calculations.length === 0"
          aria-label="Toggle Collapse All"
        >
          <CompressOutlined v-if="isCollapsed" class="text-base" style="line-height: 1; margin: 0" />
          <ExpandOutlined v-else class="text-base" style="line-height: 1; margin: 0" />
        </Button>
      </a-tooltip>

      <Button
        type="primary"
        shape="circle"
        size="large"
        class="flex items-center justify-center"
        @click="addCalculation"
        :title="`Add ${arrayCalculationField.item_schema!.display_name}`"
        :disabled="isAddDisabled"
        :aria-disabled="isAddDisabled"
      >
        <PlusOutlined class="text-base" style="line-height: 1; margin: 0" />
      </Button>
    </div>

    <div class="mt-5">
      <div
        class="calculation-mapping-row"
        v-for="(calculation, index) in calculations"
        :key="calculation.id"
        @dragover="handleDragOver($event, index)"
        @dragleave="handleDragLeave"
        @drop="handleDrop($event, index)"
        :class="{ 'drop-zone-active': dragOverIndex === index }"
      >
        <div
          class="drag-handle"
          draggable="true"
          @dragstart="handleDragStart($event, index)"
          @dragend="handleDragEnd"
          tabindex="0"
          role="button"
          :aria-grabbed="draggedIndex === index"
          aria-label="Drag to reorder"
        >
          <HolderOutlined class="text-base" style="line-height: 1; margin: 0" />
        </div>

        <CalculationConfiguration
          :configuration-level="props.configurationLevel + 1"
          :calculation="calculation"
          :available-columns="availableColumns"
          :available-params="availableParams"
          @configuration-updated="handleConfigurationUpdate"
          :calculation-schemas="calculationSchemas"
          :is-condensed-layout="props.isCondensedLayout"
          :global-collapsed="globalCollapsed"
        />

        <div class="action-buttons ml-2 flex flex-col items-center">
          <a-tooltip placement="left">
            <template #title>Move Up</template>
            <Button
              type="default"
              shape="circle"
              size="small"
              class="compact-action-btn"
              @click="moveUp(index)"
              :disabled="index === 0"
              :aria-disabled="index === 0"
            >
              <ArrowUpOutlined class="compact-icon" style="line-height: 1; margin: 0" />
            </Button>
          </a-tooltip>

          <a-tooltip placement="left">
            <template #title>
              {{ `Remove ${arrayCalculationField.item_schema!.display_name}` }}
            </template>
            <Button
              type="default"
              shape="circle"
              size="small"
              class="compact-action-btn ant-btn-dangerous my-1"
              @click="removeCalculation(index)"
            >
              <Trash2 class="compact-icon" style="line-height: 1; margin: 0" />
            </Button>
          </a-tooltip>

          <a-tooltip placement="left">
            <template #title>Move Down</template>
            <Button
              type="default"
              shape="circle"
              size="small"
              class="compact-action-btn"
              @click="moveDown(index)"
              :disabled="index === calculations.length - 1"
              :aria-disabled="index === calculations.length - 1"
            >
              <ArrowDownOutlined class="compact-icon" style="line-height: 1; margin: 0" />
            </Button>
          </a-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .calculation-array-object-builder-container {
    display: flex;
    flex-direction: column;
  }

  .calculation-mapping-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 15px;
  }

  .drag-handle {
    cursor: grab;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: transparent;
    color: var(--muted-foreground, #6b7280);
  }

  .compact-action-btn {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }

  .compact-action-btn.compact-action-btn.ant-btn-dangerous {
    padding: 0;
  }

  .compact-icon {
    display: inline-flex;
  }

  /* Ensure SVG-based icons (lucide) respect compact sizing */
  .compact-icon,
  .compact-icon svg {
    width: 14px !important;
    height: 14px !important;
    line-height: 1 !important;
  }

  .calculation-mapping-row.drop-zone-active {
    outline: 2px dashed rgba(24, 144, 255, 0.5);
    background: rgba(24, 144, 255, 0.03);
  }

  .action-buttons .ant-btn {
    padding: 3px;
  }

  select {
    padding: 4px;
  }
</style>
